import React from 'react';
import { UseFormReturn, useFieldArray } from 'react-hook-form';
import { OnboardingSchema } from '@features/onboarding/schema/onboarding';
import { format, parseISO } from 'date-fns';

interface ApplicantsStepProps {
  form: UseFormReturn<OnboardingSchema | any>;
  formData: Partial<OnboardingSchema>;
  updateFormData: (data: Partial<OnboardingSchema>) => void;
}

export const ApplicantsStep: React.FC<ApplicantsStepProps> = ({ form }) => {
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'applicants',
  });

  const addApplicant = (type: 'spouse' | 'dependent') => {
    append({
      applicant: type,
      firstName: '',
      midName: '',
      lastName: '',
      gender: undefined,
      householdIncome: undefined,
      dateOfBirth: {
        month: '',
        day: '',
        year: '',
      },
      height: {
        ft: undefined,
        in: undefined,
      },
      weight: undefined,
      nicotine: false,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Add Dependents
        </h2>
        <p className="text-gray-600 mb-6">
          Add any dependents who will be covered under your benefits. You can add or remove them from any benefits you select.
        </p>
      </div>

      {fields.map((field: any, index) => (
        <div key={field.id} className="border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {field.applicant === 'primary' ? 'Primary Applicant' : 
               field.applicant === 'spouse' ? 'Spouse' : 'Dependent'}
            </h3>
            {index > 0 && (
              <button
                type="button"
                onClick={() => remove(index)}
                className="text-red-600 hover:text-red-800 text-sm font-medium"
              >
                Remove
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                type="text"
                {...form.register(`applicants.${index}.firstName`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                {...form.register(`applicants.${index}.lastName`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gender
              </label>
              <select
                {...form.register(`applicants.${index}.gender`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            {field.applicant === 'primary' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Household Income <span className="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  {...form.register('householdIncome')}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={form.formState.errors.householdIncome ? { 
                    outline: 'var(--color-error, #ef4444) solid 3px',
                    backgroundColor: '#fee6e6'
                  } : {}}
                  placeholder="Annual income"
                  aria-invalid={!!form.formState.errors.householdIncome}
                />
                {form.formState.errors.householdIncome && (
                  <p className="mt-1 text-sm text-red-600">
                    {String(form.formState.errors.householdIncome.message)}
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of Birth
              </label>
              <div className="grid grid-cols-3 gap-2">
                <select
                  {...form.register(`applicants.${index}.dateOfBirth.month`)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Month</option>
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                      {new Date(0, i).toLocaleDateString('en', { month: 'long' })}
                    </option>
                  ))}
                </select>
                <select
                  {...form.register(`applicants.${index}.dateOfBirth.day`)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Day</option>
                  {Array.from({ length: 31 }, (_, i) => (
                    <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                      {i + 1}
                    </option>
                  ))}
                </select>
                <select
                  {...form.register(`applicants.${index}.dateOfBirth.year`)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Year</option>
                  {Array.from({ length: 100 }, (_, i) => {
                    const year = parseISO(format(new Date(), 'yyyy-MM-dd')).getFullYear() - i;
                    return (
                      <option key={year} value={String(year)}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Height
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  {...form.register(`applicants.${index}.height.ft`)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Feet"
                  min="0"
                  max="8"
                />
                <input
                  type="number"
                  {...form.register(`applicants.${index}.height.in`)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Inches"
                  min="0"
                  max="11"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Weight (lbs)
              </label>
              <input
                type="number"
                {...form.register(`applicants.${index}.weight`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Weight in pounds"
                min="7"
                max="800"
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  {...form.register(`applicants.${index}.nicotine`)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Uses nicotine products
                </span>
              </label>
            </div>
          </div>
        </div>
      ))}

      <div className="flex gap-2">
        {!fields.some((f: any) => f.applicant === 'spouse') && (
          <button
            type="button"
            onClick={() => addApplicant('spouse')}
            className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
          >
            Add Spouse
          </button>
        )}
        <button
          type="button"
          onClick={() => addApplicant('dependent')}
          className="px-4 py-2 text-sm font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100"
        >
          Add Dependent
        </button>
      </div>
    </div>
  );
}; 